import { useNavigate } from 'react-router-dom';
import { useGoal } from '../context/GoalContext';
import Card from '../components/Card';
import ProgressBar from '../components/ProgressBar';
import BottomNav from '../components/BottomNav';
import { formatCurrency, formatDate } from '../utils/calculations';
import styles from './HomeScreen.module.css';

const ACCOUNTS = [
  { id: 1, name: 'Checking',     number: '1180', balance: 45240  },
  { id: 2, name: 'Savings',      number: '4892', balance: 124800 },
  { id: 3, name: 'Travel Fund',  number: '7723', balance: 15420  },
];

const TOTAL_BALANCE = ACCOUNTS.reduce((s, a) => s + a.balance, 0);

const AccountIcon = ({ name }) => {
  const icons = {
    Checking:    <path d="M3 9h18M7 15h2m4 0h2M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>,
    Savings:     <path d="M3 10h18M3 6h18M6 14h.01M10 14h4M6 18h12a2 2 0 002-2V8a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>,
    'Travel Fund':<path d="M12 19V5m0 0l-4 4m4-4l4 4M3 19h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>,
  };
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {icons[name] || icons.Savings}
    </svg>
  );
};

const iconColors = {
  Checking:     { bg: '#E8F5FD', fg: '#2196F3' },
  Savings:      { bg: '#E6F9F5', fg: '#00B8A9' },
  'Travel Fund':{ bg: '#FFF3E0', fg: '#FF9800' },
};

const HomeScreen = () => {
  const navigate = useNavigate();
  const { savedGoals } = useGoal();

  return (
    <div className={styles.screen}>
      <div className={styles.content}>

        {/* Top bar */}
        <div className={styles.topBar}>
          <div>
            <p className={styles.greetingText}>Good morning,</p>
            <h1 className={styles.name}>Sebin</h1>
          </div>
          <button className={styles.notifBtn} aria-label="Notifications">
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path d="M11 3a6 6 0 00-6 6v2.5L3.5 14h15L17 11.5V9a6 6 0 00-6-6z" stroke="#1A1A1A" strokeWidth="1.6" strokeLinejoin="round"/>
              <path d="M9.5 17.5a1.5 1.5 0 003 0" stroke="#1A1A1A" strokeWidth="1.6" strokeLinecap="round"/>
              <circle cx="16" cy="5" r="3" fill="#00B8A9"/>
            </svg>
          </button>
        </div>

        {/* Total balance */}
        <Card className={styles.balanceCard}>
          <p className={styles.balanceLabel}>Total balance</p>
          <h2 className={styles.balanceAmount}>{formatCurrency(TOTAL_BALANCE)}</h2>
          <div className={styles.balanceRow}>
            <div className={styles.balanceStat}>
              <span className={styles.statLabel}>This month</span>
              <span className={styles.statValue}>+{formatCurrency(38400)}</span>
            </div>
            <div className={styles.divider} />
            <div className={styles.balanceStat}>
              <span className={styles.statLabel}>Spent</span>
              <span className={styles.statValue}>-{formatCurrency(21350)}</span>
            </div>
            <div className={styles.divider} />
            <div className={styles.balanceStat}>
              <span className={styles.statLabel}>Saved</span>
              <span className={styles.statValue}>+{formatCurrency(17050)}</span>
            </div>
          </div>
        </Card>

        {/* Accounts section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Accounts</h3>
            <button className={styles.sectionLink}>Manage</button>
          </div>
          <Card className={styles.accountsCard}>
            {ACCOUNTS.map((acct, i) => {
              const colors = iconColors[acct.name] || iconColors.Savings;
              return (
                <div
                  key={acct.id}
                  className={`${styles.accountRow} ${i < ACCOUNTS.length - 1 ? styles.accountRowBorder : ''}`}
                >
                  <div className={styles.accountIcon} style={{ background: colors.bg, color: colors.fg }}>
                    <AccountIcon name={acct.name} />
                  </div>
                  <div className={styles.accountMeta}>
                    <span className={styles.accountName}>{acct.name}</span>
                    <span className={styles.accountNumber}>••••{acct.number}</span>
                  </div>
                  <span className={styles.accountBalance}>{formatCurrency(acct.balance)}</span>
                </div>
              );
            })}
          </Card>
        </div>

        {/* Goals section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Your goals</h3>
            {savedGoals.length > 0 && (
              <button className={styles.sectionLink} onClick={() => navigate('/goals')}>
                See all
              </button>
            )}
          </div>

          {savedGoals.length > 0 ? (
            <>
              {savedGoals.map(goal => (
                <Card key={goal.id} className={styles.goalCard} onClick={() => navigate(`/goal/${goal.id}`)}>
                  <div className={styles.goalHeader}>
                    <div>
                      <p className={styles.goalName}>{goal.name}</p>
                      <p className={styles.goalDate}>Target: {formatDate(goal.deadline)}</p>
                    </div>
                    <div className={styles.goalAmounts}>
                      <span className={styles.goalCurrent}>{formatCurrency(goal.currentSaved)}</span>
                      <span className={styles.goalTarget}>of {formatCurrency(goal.amount)}</span>
                    </div>
                  </div>
                  <ProgressBar current={goal.currentSaved} target={goal.amount} height={8} />
                </Card>
              ))}
            </>
          ) : (
            <p className={styles.noGoals}>No goals yet</p>
          )}

          {/* Create goal CTA */}
          <Card className={styles.ctaCard} onClick={() => navigate('/create-goal/step1')}>
            <div className={styles.ctaIcon}>
              <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                <circle cx="13" cy="13" r="11" fill="#E6F9F5"/>
                <path d="M13 8.5V17.5M8.5 13H17.5" stroke="#00B8A9" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className={styles.ctaText}>
              <span className={styles.ctaTitle}>Create a goal</span>
              <span className={styles.ctaSub}>Start saving for what matters</span>
            </div>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 3.5L10.5 8L6 12.5" stroke="#BDBDBD" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Card>
        </div>

      </div>
      <BottomNav />
    </div>
  );
};

export default HomeScreen;
